/**
 * BeschaeftigungsWechselModal.jsx
 *
 * Beschäftigungsart wechseln ohne Kündigung + Neuanlage.
 * - Step 1: Neue Beschäftigungsart, Lohn, Stunden, Wirksam-ab, SV-Hinweis
 * - Step 2 (nur Minijob): Personalbogen-Mini-Abfrage (weitere Beschäftigungen, SV-Klärung)
 * - Step 3 (nur Minijob): Befreiungsantrag Rentenversicherung (§ 6 SGB VI)
 * - Ergebnis: Unterschriftsmethode wählen (Vor Ort / Per E-Mail) für jeden Vertrag
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronRight, ChevronLeft, AlertTriangle, CheckCircle2,
  Loader2, ArrowRightLeft, FileText, Info,
  Mail, Smartphone, Key, ExternalLink, Shield, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BESCHAEFTIGUNGSARTEN = [
  { value: 'vollzeit',    label: 'Vollzeit',           sv: 'pflicht' },
  { value: 'teilzeit',    label: 'Teilzeit',           sv: 'pflicht' },
  { value: 'minijob',     label: 'Aushilfe (Minijob)', sv: 'geringfügig' },
  { value: 'werkstudent', label: 'Werkstudent',        sv: 'sonderstatus' },
];

function isGeringfuegig(art) {
  return art === 'minijob';
}

function svWarning(altArt, neueArt) {
  const altGF = isGeringfuegig(altArt);
  const neueGF = isGeringfuegig(neueArt);
  if (altGF === neueGF) return null;
  if (!altGF && neueGF) {
    return 'Dieser Wechsel von einer sozialversicherungspflichtigen zu einer geringfügigen Beschäftigung erfordert eine Abmeldung bei der Krankenkasse und ggf. eine Anmeldung bei der Minijob-Zentrale – bitte Lohnbüro/Steuerberater informieren.';
  }
  return 'Dieser Wechsel von einer geringfügigen zu einer sozialversicherungspflichtigen Beschäftigung erfordert eine Anmeldung bei der Krankenkasse und Abmeldung bei der Minijob-Zentrale – bitte Lohnbüro/Steuerberater informieren.';
}

export function BeschaeftigungsWechselModal({ open, onClose, employee, bogen, onSaved }) {
  const currentArt = bogen?.arbeitgeber?.arbeitszeit || bogen?.arbeitgeber?.beschaeftigungsart || '';
  const steuer = bogen?.steuer || {};

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [savedResult, setSavedResult] = useState(null);

  // Step 1
  const [neueArt, setNeueArt] = useState('');
  const [neuerLohn, setNeuerLohn] = useState(bogen?.arbeitgeber?.stundenlohn || '');
  const [neueStunden, setNeueStunden] = useState(bogen?.arbeitgeber?.stunden_pro_woche || '');
  const [neueMonatsstunden, setNeueMonatsstunden] = useState(bogen?.arbeitgeber?.stunden_pro_monat || '');
  const [wirksam, setWirksam] = useState('');

  // Step 2 (Minijob)
  const [weitereBeschaeftigungen, setWeitereBeschaeftigungen] = useState(
    bogen?.arbeitgeber?.weitere_beschaeftigungen || ''
  );
  const [weitereBeschaeftigungenInfo, setWeitereBeschaeftigungenInfo] = useState(
    bogen?.arbeitgeber?.weitere_beschaeftigungen_info || ''
  );
  const [steuerklasse, setSteuerklasse] = useState(steuer.steuerklasse || '');
  const [konfessionskennzeichen, setKonfessionskennzeichen] = useState(steuer.konfession || '');

  // Step 3 ist reine INFORMATION – die RV-Entscheidung trifft der Mitarbeiter selbst (Link/Vor Ort).

  const isMinijob = isGeringfuegig(neueArt);
  const warning = neueArt ? svWarning(currentArt, neueArt) : null;

  const STEPS = isMinijob ? 3 : 1;

  const resetAll = () => {
    setStep(1); setNeueArt(''); setNeuerLohn(bogen?.arbeitgeber?.stundenlohn || '');
    setNeueStunden(bogen?.arbeitgeber?.stunden_pro_woche || '');
    setNeueMonatsstunden(bogen?.arbeitgeber?.stunden_pro_monat || '');
    setWirksam('');
    setWeitereBeschaeftigungen(bogen?.arbeitgeber?.weitere_beschaeftigungen || '');
    setWeitereBeschaeftigungenInfo(bogen?.arbeitgeber?.weitere_beschaeftigungen_info || '');
    setSteuerklasse(steuer.steuerklasse || ''); setKonfessionskennzeichen(steuer.konfession || '');
    setSaving(false); setSavedResult(null);
  };

  const canNext = () => {
    if (step === 1) return neueArt && wirksam;
    if (step === 2) return weitereBeschaeftigungen !== '';
    // Step 3 ist reine Information – der Mitarbeiter entscheidet selbst, keine Admin-Pflichtwahl.
    if (step === 3) return true;
    return false;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        neue_beschaeftigungsart: neueArt,
        neuer_stundenlohn: neuerLohn || null,
        neue_wochenstunden: neueStunden || null,
        neue_monatsstunden: (isMinijob && neueMonatsstunden) ? neueMonatsstunden : null,
        wirksam_ab: wirksam,
        auto_create_contract: true,
      };
      if (isMinijob) {
        payload.weitere_beschaeftigungen = weitereBeschaeftigungen;
        payload.weitere_beschaeftigungen_info = weitereBeschaeftigungenInfo;
        // RV-Entscheidung wird vom Admin NICHT mitgegeben – der Mitarbeiter entscheidet selbst.
        if (steuerklasse || konfessionskennzeichen) {
          payload.aktualisierte_steuer = {
            ...(steuerklasse ? { steuerklasse } : {}),
            ...(konfessionskennzeichen ? { konfession: konfessionskennzeichen } : {}),
          };
        }
      }
      const res = await axios.post(
        `${API}/api/personalbogen/employee/${employee.id}/change-employment`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSavedResult(res.data);
      toast.success(res.data.scheduled ? 'Beschäftigungswechsel geplant' : 'Beschäftigungsart erfolgreich geändert');
      onSaved && onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const artLabel = BESCHAEFTIGUNGSARTEN.find(a => a.value === currentArt)?.label || currentArt || '–';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { resetAll(); onClose(); } }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowRightLeft className="h-4 w-4 text-primary" />
            Beschäftigungsart ändern – {employee?.name}
          </DialogTitle>
        </DialogHeader>

        {/* Schritt-Anzeige */}
        {!savedResult && STEPS > 1 && (
          <div className="flex items-center gap-1 mb-1">
            {Array.from({ length: STEPS }, (_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${
                i + 1 <= step ? 'bg-primary' : 'bg-border'
              }`} />
            ))}
          </div>
        )}

        <div className="space-y-4 py-1">

          {/* ── ERGEBNIS: Unterschriftsmethode wählen ──────────────────── */}
          {savedResult && savedResult.scheduled && (
            <div className="space-y-4" data-testid="change-scheduled-result">
              <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl p-4">
                <Clock className="h-6 w-6 text-blue-600 shrink-0" />
                <div>
                  <p className="font-semibold text-blue-800 dark:text-blue-300">Beschäftigungswechsel geplant</p>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">{savedResult.message}</p>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-muted/30 rounded-xl p-3 text-xs text-muted-foreground">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <p>Bis zum Gültigkeitsdatum bleiben Anzeige, Rechte und Dokumente unverändert.
                   Am Stichtag wird der Wechsel automatisch übernommen – die erforderlichen
                   Unterschriften/Dokumente werden dann bereitgestellt. Kein geöffneter Browser nötig.</p>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => { resetAll(); onClose(); }} data-testid="close-scheduled-btn">Schließen</Button>
              </div>
            </div>
          )}

          {savedResult && !savedResult.scheduled && (
            <div className="space-y-4">
              {/* Erfolgs-Header */}
              <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl p-4">
                <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
                <div>
                  <p className="font-semibold text-green-800 dark:text-green-300">Beschäftigungsart geändert!</p>
                  <p className="text-xs text-green-700 dark:text-green-400">{savedResult.message}</p>
                </div>
              </div>

              {/* Für jedes Dokument: Unterschriftsmethode wählen */}
              {/* Für jeden neuen Arbeitsvertrag: SigningMethodCard */}
              {savedResult.new_signing_id && (
                <SigningMethodCard
                  doc={{
                    id: savedResult.new_signing_id,
                    token: savedResult.new_signing_token,
                    pin: savedResult.new_signing_pin,
                    label: 'Neuer Arbeitsvertrag',
                    icon: <FileText className="h-4 w-4 text-blue-600" />,
                    color: 'blue',
                  }}
                  employee={employee}
                  API={API}
                />
              )}

              {/* RV-Entscheidung: Mitarbeiter entscheidet SELBST per Link + PIN (kein Admin-Fingieren) */}
              {savedResult.rv_decision_pending && savedResult.rv_decision_token && (
                <RvDecisionCard
                  token={savedResult.rv_decision_token}
                  pin={savedResult.rv_decision_pin}
                  employee={employee}
                  API={API}
                />
              )}

              {!savedResult.new_signing_id && !savedResult.rv_decision_pending && (
                <p className="text-xs text-muted-foreground text-center">
                  Keine passende Vertragsvorlage gefunden — bitte Vorlage im Vertragsmanager anlegen.
                </p>
              )}

              <div className="flex justify-end">
                <Button onClick={() => { resetAll(); onClose(); }}>Schließen</Button>
              </div>
            </div>
          )}

          {/* ── STEP 1: Kernänderungen ────────────────────────────────── */}
          {!savedResult && step === 1 && (
            <div className="space-y-4">
              <div className="bg-muted/40 rounded-xl p-3 text-sm">
                <p className="text-xs text-muted-foreground">Aktuelle Beschäftigungsart</p>
                <p className="font-semibold">{artLabel}</p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">Neue Beschäftigungsart *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {BESCHAEFTIGUNGSARTEN.map(a => (
                    <button key={a.value} type="button" onClick={() => setNeueArt(a.value)}
                      disabled={a.value === currentArt}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        neueArt === a.value
                          ? 'border-primary bg-primary/5'
                          : a.value === currentArt
                          ? 'border-border opacity-40 cursor-not-allowed'
                          : 'border-border hover:border-primary/40'
                      }`}>
                      <p className="font-medium text-sm">{a.label}</p>
                      <p className="text-xs text-muted-foreground">{a.sv === 'pflicht' ? 'SV-pflichtig' : a.sv === 'geringfügig' ? 'Geringfügig' : 'Sonderstatus'}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* SV-Warnbanner */}
              {warning && (
                <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 rounded-xl p-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 dark:text-amber-200">{warning}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Neuer Stundenlohn (€)</Label>
                  <Input value={neuerLohn} onChange={e => setNeuerLohn(e.target.value)}
                    placeholder="z.B. 14.50" className="h-9 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Wochenstunden</Label>
                  <Input value={neueStunden} onChange={e => setNeueStunden(e.target.value)}
                    placeholder="z.B. 10" className="h-9 text-sm" />
                </div>
              </div>

              {/* Monatsstunden — nur bei Minijob */}
              {isMinijob && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    Monatsstunden <span className="text-muted-foreground/60">(für Arbeitsvertrag & Minijob-Zentrale)</span>
                  </Label>
                  <Input
                    value={neueMonatsstunden}
                    onChange={e => setNeueMonatsstunden(e.target.value)}
                    placeholder="z.B. 40"
                    className="h-9 text-sm"
                    data-testid="monatsstunden-input"
                  />
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-sm font-semibold">Wirksam ab * <span className="font-normal text-muted-foreground text-xs">(TT.MM.JJJJ – heute oder Zukunft)</span></Label>
                <Input value={wirksam} onChange={e => setWirksam(e.target.value)}
                  placeholder="z.B. 01.08.2026" className="h-9 text-sm" />
                {wirksam && /^\d{2}\.\d{2}\.\d{4}$/.test(wirksam) && (
                  <p className="text-xs text-muted-foreground">
                    Alte Beschäftigung endet automatisch am {(() => {
                      try {
                        const [d, m, y] = wirksam.split('.');
                        const dt = new Date(`${y}-${m}-${d}`);
                        dt.setDate(dt.getDate() - 1);
                        return dt.toLocaleDateString('de-DE');
                      } catch { return '–'; }
                    })()}.
                  </p>
                )}
              </div>

              <div className="flex items-start gap-2 bg-muted/30 rounded-xl p-3 text-xs text-muted-foreground">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <p>Die Stammdaten (Name, SV-Nummer, IBAN, Personalbogen) bleiben vollständig erhalten. Es wird nur die Beschäftigung geändert – keine Kündigung, keine Neuanlage.</p>
              </div>
            </div>
          )}

          {/* ── STEP 2: Minijob-Personalbogen-Abfrage ────────────────── */}
          {!savedResult && step === 2 && isMinijob && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Bei einem Wechsel in einen Minijob müssen folgende Angaben für die Minijob-Zentrale aktuell sein:</p>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Weitere Beschäftigungen *</Label>
                <p className="text-xs text-muted-foreground">Hat {employee?.name?.split(' ')[0]} noch andere Arbeitgeber neben diesem Minijob?</p>
                <div className="flex gap-2">
                  {[['nein', 'Nein (nur dieser Minijob)'], ['ja', 'Ja (weitere Beschäftigung vorhanden)']].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setWeitereBeschaeftigungen(v)}
                      className={`flex-1 py-2.5 text-sm rounded-xl border-2 font-medium transition-all ${
                        weitereBeschaeftigungen === v ? 'border-primary bg-primary/5 text-primary' : 'border-border'
                      }`}>{l}</button>
                  ))}
                </div>
                {weitereBeschaeftigungen === 'ja' && (
                  <div className="space-y-1 pl-2">
                    <Label className="text-xs text-muted-foreground">Angaben zur weiteren Beschäftigung (Arbeitgeber, Art, Stunden)</Label>
                    <textarea value={weitereBeschaeftigungenInfo} onChange={e => setWeitereBeschaeftigungenInfo(e.target.value)}
                      rows={3} placeholder="z.B. Hauptjob bei Firma XY, 20 Std./Woche, SV-pflichtig"
                      className="w-full text-sm border rounded-xl px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                    <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-3">
                      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 dark:text-amber-200">Wenn mehrere geringfügige Beschäftigungen zusammen die Minijob-Grenze überschreiten, entsteht SV-Pflicht. Bitte Lohnbüro/Steuerberater informieren.</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3 border-t pt-3">
                <p className="text-sm font-semibold">Lohnsteuer-Angaben bestätigen</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Steuerklasse</Label>
                    <Input value={steuerklasse} onChange={e => setSteuerklasse(e.target.value)}
                      placeholder="z.B. 1" className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Kirchensteuerkennzeichen</Label>
                    <Input value={konfessionskennzeichen} onChange={e => setKonfessionskennzeichen(e.target.value)}
                      placeholder="z.B. –" className="h-8 text-sm" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Hinweis: Bei Minijob kann pauschale Lohnsteuer (2 %) über den Arbeitgeber möglich sein. Klärung mit Lohnbüro empfohlen.</p>
              </div>
            </div>
          )}

          {/* ── STEP 3: RV-Entscheidung (trifft der MITARBEITER selbst) ── */}
          {!savedResult && step === 3 && isMinijob && (
            <div className="space-y-4" data-testid="rv-info-step">
              <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl p-4">
                <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm text-blue-800 dark:text-blue-200">Rentenversicherung (§ 6 SGB VI)</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    Minijobber sind grundsätzlich rentenversicherungspflichtig (3,6 % des Lohns) und können sich auf
                    eigenen Antrag davon befreien lassen. Diese Entscheidung – inklusive der Kenntnisnahme des Merkblatts –
                    trifft <strong>{employee?.name?.split(' ')[0]} selbst</strong>. Sie darf nicht vorweggenommen werden.
                  </p>
                </div>
              </div>

              <div className="space-y-2 border border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-900/10 rounded-xl p-4">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4 text-violet-600 shrink-0" />
                  So geht es nach dem Speichern weiter:
                </p>
                <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
                  <li>Es entsteht KEIN vorausgefüllter Befreiungsantrag – der Status bleibt zunächst „offen“.</li>
                  <li>Sie erhalten einen Mitarbeiter-Link + PIN, den {employee?.name?.split(' ')[0]} vor Ort oder per E-Mail nutzen kann.</li>
                  <li>{employee?.name?.split(' ')[0]} liest das Merkblatt, bestätigt die Kenntnisnahme und wählt selbst: Befreiung beantragen oder versicherungspflichtig bleiben.</li>
                  <li>Erst mit dieser eigenen Entscheidung entsteht ggf. der unterschriebene Befreiungsantrag.</li>
                </ul>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        {!savedResult && (
          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              {step === 1 ? 'Abbrechen' : 'Zurück'}
            </Button>
            {(step < STEPS) && (
              <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()}>
                Weiter <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === STEPS && (
              <Button onClick={handleSave} disabled={saving || !canNext()}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Änderung speichern
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ── SigningMethodCard: Vor Ort / Per E-Mail Auswahl ───────────────────────────
function SigningMethodCard({ doc, employee, API }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const borderColor = doc.color === 'violet' ? 'border-violet-200 dark:border-violet-800' : 'border-blue-200 dark:border-blue-800';
  const bgColor = doc.color === 'violet' ? 'bg-violet-50 dark:bg-violet-900/20' : 'bg-blue-50 dark:bg-blue-900/20';

  const handleVorOrt = () => {
    // Öffnet die öffentliche Signierseite im selben Tab → Mitarbeiter/Admin unterschreibt direkt
    if (doc.token) {
      window.open(`/vertrag/${doc.token}`, '_blank', 'noopener');
    } else {
      toast.error('Kein Signierlink verfügbar');
    }
  };

  const handleEmail = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/contracts/signing/${doc.id}/send-signing-link-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSent(true);
      toast.success(`Signierlink an ${employee?.name || 'Mitarbeiter'} gesendet`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`${bgColor} ${borderColor} border rounded-xl p-4 space-y-3`}
         data-testid={`signing-card-${doc.id}`}>
      <p className="text-sm font-semibold flex items-center gap-2">
        {doc.icon}
        {doc.label}
      </p>

      {/* PIN anzeigen (Admin informiert Mitarbeiter mündlich) */}
      {doc.pin && (
        <div className="flex items-center gap-2 bg-white dark:bg-gray-900/50 rounded-lg px-3 py-1.5 border">
          <Key className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground">PIN für Mitarbeiter:</span>
          <span className="font-mono font-bold text-sm tracking-widest" data-testid="signing-pin">{doc.pin}</span>
          <span className="text-xs text-muted-foreground ml-auto">(mündlich mitteilen)</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {/* Vor Ort unterschreiben */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleVorOrt}
          disabled={!doc.token}
          className="gap-1.5 text-xs"
          data-testid="btn-vor-ort"
        >
          <Smartphone className="h-3.5 w-3.5" />
          Vor Ort unterschreiben
          <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
        </Button>

        {/* Per E-Mail senden */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleEmail}
          disabled={sending || sent}
          className={`gap-1.5 text-xs ${sent ? 'border-green-500 text-green-700' : ''}`}
          data-testid="btn-email"
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
           sent ? <CheckCircle2 className="h-3.5 w-3.5" /> :
           <Mail className="h-3.5 w-3.5" />}
          {sent ? 'E-Mail gesendet' : 'Per E-Mail senden'}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        <strong>Vor Ort:</strong> Mitarbeiter unterschreibt sofort auf diesem Gerät. &nbsp;
        <strong>E-Mail:</strong> Link an Mitarbeiter, unterschreibt später remote.
      </p>
    </div>
  );
}

// ── RvDecisionCard: Mitarbeiter trifft die RV-Entscheidung selbst (Link + PIN) ──
function RvDecisionCard({ token, pin, employee, API }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleVorOrt = () => {
    if (token) window.open(`/personalbogen/${token}`, '_blank', 'noopener');
    else toast.error('Kein Mitarbeiter-Link verfügbar');
  };

  const handleEmail = async () => {
    setSending(true);
    try {
      const authToken = localStorage.getItem('token');
      await axios.post(
        `${API}/api/personalbogen/public/${token}/send-pin-email`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setSent(true);
      toast.success(`Link + PIN an ${employee?.name || 'Mitarbeiter'} gesendet`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl p-4 space-y-3"
         data-testid="rv-decision-card">
      <p className="text-sm font-semibold flex items-center gap-2">
        <Shield className="h-4 w-4 text-violet-600" />
        Rentenversicherung – {employee?.name?.split(' ')[0] || 'Mitarbeiter'} entscheidet selbst
      </p>
      <p className="text-xs text-muted-foreground">
        Status: <strong>offen</strong>. {employee?.name?.split(' ')[0] || 'Der Mitarbeiter'} liest das Merkblatt und wählt
        anschließend selbst zwischen Befreiung und Versicherungspflicht. Es wurde KEINE Entscheidung vorweggenommen.
      </p>

      {pin && (
        <div className="flex items-center gap-2 bg-white dark:bg-gray-900/50 rounded-lg px-3 py-1.5 border">
          <Key className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground">PIN für Mitarbeiter:</span>
          <span className="font-mono font-bold text-sm tracking-widest" data-testid="rv-decision-pin">{pin}</span>
          <span className="text-xs text-muted-foreground ml-auto">(mündlich mitteilen)</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" size="sm" onClick={handleVorOrt} disabled={!token}
          className="gap-1.5 text-xs" data-testid="rv-decision-vor-ort">
          <Smartphone className="h-3.5 w-3.5" />
          Vor Ort entscheiden
          <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
        </Button>
        <Button variant="outline" size="sm" onClick={handleEmail} disabled={sending || sent}
          className={`gap-1.5 text-xs ${sent ? 'border-green-500 text-green-700' : ''}`} data-testid="rv-decision-email">
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
           sent ? <CheckCircle2 className="h-3.5 w-3.5" /> :
           <Mail className="h-3.5 w-3.5" />}
          {sent ? 'E-Mail gesendet' : 'Per E-Mail senden'}
        </Button>
      </div>
    </div>
  );
}
